import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { CheckCircle2, Mail, Truck, Paintbrush, Instagram, Gift, ArrowRight, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const Step = ({ icon: Icon, title, text, color }) => (
  <div className="flex gap-3">
    <span className={`grid place-items-center w-10 h-10 rounded-xl border-2 border-ink shrink-0 ${color}`}><Icon className="w-5 h-5 text-white" /></span>
    <div>
      <div className="font-display font-bold">{title}</div>
      <div className="text-sm text-ink/60">{text}</div>
    </div>
  </div>
);

export default function ThankYouPage() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    window.scrollTo(0, 0);
    // Celebratory confetti burst on load
    const colors = ["#FFDE59", "#FF5C9E", "#5CC8FF", "#B08CFF", "#FF8A3D"];
    const fire = (x) => confetti({ particleCount: 60, spread: 70, startVelocity: 45, origin: { x, y: 0.35 }, colors });
    fire(0.25); setTimeout(() => fire(0.5), 150); setTimeout(() => fire(0.75), 300);
  }, [orderId]);

  const firstName = order?.customer_name?.split(" ")[0] || "friend";
  const delivery = order?.delivery_method;
  const deliveryText = delivery === "mail"
    ? "Your finished caricature will be mailed to your address."
    : delivery === "both"
      ? "You'll get a digital copy by email and a printed one in the mail."
      : "Your finished caricature will be delivered to your email.";

  return (
    <div className="max-w-3xl mx-auto px-5 py-14" data-testid="thank-you-page">
      {/* Hero */}
      <div className="text-center mb-10">
        <span className="inline-grid place-items-center w-20 h-20 rounded-full bg-sun border-[3px] border-ink shadow-brut mb-5 animate-bounce">
          <PartyPopper className="w-10 h-10 text-ink" />
        </span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl">Thank you, {firstName}! 🎉</h1>
        <p className="text-ink/70 text-lg mt-3 max-w-xl mx-auto">
          Your order is in — I can't wait to start sketching. You'll get an email receipt as soon as your payment is confirmed.
        </p>
        {order && <p className="text-ink/50 mt-2 text-sm">Order <span className="font-bold">#{order.id.slice(0, 8)}</span></p>}
      </div>

      {/* Order summary */}
      {order && (
        <div className="brut-card p-6 mb-6" data-testid="thankyou-summary">
          <h2 className="font-display font-bold text-xl mb-3">Your order</h2>
          {order.items.map((i) => (
            <div key={i.lookup_key} className="flex justify-between py-1.5 text-sm border-b border-dashed border-ink/20">
              <span>{i.name} × {i.quantity}</span><span className="font-bold">${(i.price * i.quantity).toFixed(2)}</span>
            </div>
          ))}
          {order.discount > 0 && (
            <div className="flex justify-between py-1.5 text-tangerine font-bold text-sm">
              <span>Discount ({order.discount_reason})</span><span>−${order.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-t-2 border-ink mt-2 text-lg">
            <span className="font-display font-bold">Total</span>
            <span className="font-display font-bold" data-testid="thankyou-total">${order.total.toFixed(2)}</span>
          </div>
          {order.occasion && <span className="tag-pill bg-sky mt-3">For: {order.occasion}</span>}
        </div>
      )}

      {/* What happens next */}
      <div className="brut-card p-6 mb-6">
        <h2 className="font-display font-bold text-xl mb-4">What happens next</h2>
        <div className="space-y-4">
          <Step icon={CheckCircle2} color="bg-bubble" title="We confirm your payment"
            text="I'll check for your Venmo/Cash App payment (usually within a few hours) and email your receipt." />
          <Step icon={Paintbrush} color="bg-grape" title="Your caricature gets hand-drawn"
            text="Every piece is drawn by hand to capture exactly what makes you you." />
          <Step icon={delivery === "mail" ? Truck : Mail} color="bg-tangerine" title="You receive your art"
            text={deliveryText} />
        </div>
      </div>

      {/* Referral / share */}
      <div className="brut-card p-6 mb-8 bg-grape/15" data-testid="thankyou-referral">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-sun border-2 border-ink shrink-0"><Gift className="w-5 h-5" /></span>
          <div>
            <h3 className="font-display font-bold text-lg">Spread the joy — give 10% off</h3>
            <p className="text-ink/70 text-sm mt-1">Tell a friend to mention your name when they order and they'll get <b>10% off</b> their first caricature. Thanks for sharing the love!</p>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 justify-center">
        <Link to="/gallery" className="brut-btn bg-sun" data-testid="thankyou-gallery">See more examples <ArrowRight className="w-4 h-4" /></Link>
        {settings?.instagram_url && (
          <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="brut-btn bg-white" data-testid="thankyou-instagram">
            <Instagram className="w-4 h-4" /> Follow on Instagram
          </a>
        )}
        <Link to="/" className="brut-btn bg-white" data-testid="thankyou-home">Back to home</Link>
      </div>
    </div>
  );
}
